// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import Button from '@mui/material/Button'
import React, { useEffect, useState } from 'react'

import Paper from '@mui/material/Paper'
import { useTheme } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { fetchTickets, ticketPut } from '@react/fetchers/ticketFetcher'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { logOT } from '@shared/loggingShared'
import { EthAddr } from '@shared/SchemaTypes'
import { TicketCTO, TicketStatusEnum } from '@shared/TicketCTO'
import { User } from 'oidc-client-ts'
import { useNavigate } from 'react-router-dom'

export default function TicketPendingCard(props: { poolAddr?: EthAddr }): React.ReactElement {
  const {
    stageConfiguration: { serverApiBaseUrl, restAPIKey },
    cognitoUser,
  } = useOtChainState()
  const { poolOverviewCTOs } = useOtSelector((state) => state.poolState)
  const theme = useTheme()
  const [ticketsPending, setTicketsPending] = useState<TicketCTO[]>([])

  const doFetchTickets = () => {
    fetchTickets(serverApiBaseUrl, restAPIKey, TicketStatusEnum.PENDING, props.poolAddr)
      .then((_ticketsPending) => {
        setTicketsPending(_ticketsPending)
      })
      .catch((error) => {
        console.error('error fetching tickets pending', error)
      })
  }
  const navigate = useNavigate()
  useEffect(() => {
    doFetchTickets()
  }, [poolOverviewCTOs, serverApiBaseUrl, restAPIKey, cognitoUser, props.poolAddr])

  const markComplete = (ticket: TicketCTO) => {
    logOT.info('markComplete', ticket)
    ticket.ticketStatus = TicketStatusEnum.COMPLETED
    const completedTicket = {
      ...ticket,
      ticketStatus: TicketStatusEnum.COMPLETED,
    }
    ticketPut(serverApiBaseUrl, restAPIKey, cognitoUser as User, completedTicket, undefined)
      .then((_ticket) => {
        logOT.info('ticket saved', _ticket)
        doFetchTickets()
      })
      .catch((error: Error) => {
        console.error('error saving ticket', error)
      })
  }

  return (
    <Paper
      data-testid="AdminCompanyProfileList"
      sx={{
        marginTop: '20px',
        marginBottom: 4,
        marginRight: '2rem',
        marginLeft: '2rem',
        padding: '24px',
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography variant="h4" fontWeight="bold">
        Tickets Pending
      </Typography>
      {ticketsPending.length === 0 && <Typography variant="h6">No tickets pending</Typography>}
      {ticketsPending.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Label</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell>Completed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticketsPending.map((ticket) => (
                <TableRow key={ticket.id}>
                  <TableCell>
                    <Typography
                      variant="body1"
                      color="primary"
                      sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={() => navigate(ticket.link)}
                      rel="noopener noreferrer"
                    >
                      {ticket.label}
                    </Typography>
                  </TableCell>

                  <TableCell>{ticket.ticketTimestampDateString}</TableCell>

                  <TableCell>
                    {ticket.autoComplete ? (
                      <Typography variant="body1" color="primary">
                        Auto Complete
                      </Typography>
                    ) : (
                      <Button variant="contained" color="primary" onClick={() => markComplete(ticket)}>
                        Mark Complete
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}
